import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { computed, effect, inject, untracked } from '@angular/core';
import { AuthService, Entitlements, Roles } from '@shared/util-auth';
import { EntitlementsService, KonfigurationenService } from 'lion-api';
import { ClientConfigStore, SimpleMessageService, TrackingIdService } from '@shared/data';

interface UserStore {
  loaded: boolean;
  name: string;
  username: string;
  userMainGpNumber: string;
  userGruppe: string;
  roles: string[];
  entitlements: string[];
  isDzSysAdmin: boolean;
  isFkDzTrader: boolean;
  isFkTrader: boolean;
  isRefiVisibleForVr: boolean;
  isEvaluationVisibleForFk: boolean;

  hasPermitAll: boolean;
  hasInvestmentPermission: boolean;
  hasCorporatesInvestmentPermission: boolean;
  hasInvestment100Permission: boolean;
  hasRaisingPermission: boolean;
  hasRefiPermission: boolean;
  hasCancellationPermission: boolean;
  hasCorporatesCancellationPermission: boolean;
  hasBusinessStatusPermission: boolean;
  hasCorporatesBusinessStatusPermission: boolean;
  hasLimitPermission: boolean;
  hasEvaluationPermission: boolean;
  hasCorporatesExtEvaluationPermission: boolean;
  hasCorporatesDzEvaluationPermission: boolean;
  hasAdminPermission: boolean;
  hasCorporatesAdminPermission: boolean;
  hasMarginPermission: boolean;
  hasCorporatesMarginPermission: boolean;
  hasMaturityPermission: boolean;
  hasCorporatesMaturityPermission: boolean;
  hasTaskLogPermission: boolean;
  isDzTrader: boolean;
}

const initialUserState: UserStore = {
  loaded: false,
  name: '',
  username: '',
  userMainGpNumber: '',
  userGruppe: '',
  roles: [],
  entitlements: [],

  isDzSysAdmin: false,
  isFkDzTrader: false,
  isFkTrader: false,
  isRefiVisibleForVr: false,
  isEvaluationVisibleForFk: false,

  hasPermitAll: false,
  hasInvestmentPermission: false,
  hasCorporatesInvestmentPermission: false,
  hasInvestment100Permission: false,
  hasRaisingPermission: false,
  hasRefiPermission: false,
  hasCancellationPermission: false,
  hasCorporatesCancellationPermission: false,
  hasBusinessStatusPermission: false,
  hasCorporatesBusinessStatusPermission: false,
  hasLimitPermission: false,
  hasEvaluationPermission: false,
  hasCorporatesExtEvaluationPermission: false,
  hasCorporatesDzEvaluationPermission: false,
  hasAdminPermission: false,
  hasCorporatesAdminPermission: false,
  hasMarginPermission: false,
  hasCorporatesMarginPermission: false,
  hasMaturityPermission: false,
  hasCorporatesMaturityPermission: false,
  hasTaskLogPermission: false,
  isDzTrader: false
};

export const UserStore = signalStore(
  { providedIn: 'root' },
  withState(initialUserState),
  withProps(() => ({
    _authService: inject(AuthService),
    _entitlementsService: inject(EntitlementsService),
    _trackingIdService: inject(TrackingIdService),
    _simpleMessageService: inject(SimpleMessageService),
    _MESSAGE_KEY: 'customer-include',
    _konfigurationService: inject(KonfigurationenService),
    _clientConfigStore: inject(ClientConfigStore)
  })),
  withComputed((state) => {
    return {
      isDzBankUser: computed(() => {
        if (state.loaded()) {
          const isDzBankUser = Roles.DZ_ROLES.some((role) => state.roles().includes(role));
          console.log(`User is dz bank user: ${isDzBankUser}`);
          return isDzBankUser;
        }
        return false;
      }),
      isCorporateMode: computed(() => {
        if (state.loaded()) {
          return state._clientConfigStore.isLicoOrigin() || state.roles().includes(Roles.LIO_FK_DZ_TRADER);
        }
        return false;
      }),
      userType: computed<"dz" | "vr" | "fk">(() => {
        if (state.loaded()) {
          const isDzBankUser = Roles.DZ_ROLES.some((role) => state.roles().includes(role));
          if (isDzBankUser) {
            return "dz";
          }
          const isCorporate =
            state._clientConfigStore.isLicoOrigin() ||
            state.roles().includes(Roles.LICO_TRADER) ||
            state.roles().includes(Roles.LICO_READER) ||
            state.roles().includes(Roles.LIO_FK_DZ_TRADER);
          if (isCorporate) {
            return "fk";
          }
          return "vr";
        }
        return "vr";
      })
    };
  }),
  withMethods((state) => {
    const investmentPermissions = [
      Entitlements.LIO_DZ_TA_ANL,
      Entitlements.LIO_VR_TA_ANL,
      Entitlements.LIO_DZ_TE_ANL,
      Entitlements.LIO_VR_TE_ANL,
      Entitlements.LIO_DZ_ON_ANL,
      Entitlements.LIO_VR_ON_ANL,
      Entitlements.LIO_DZ_TN_ANL,
      Entitlements.LIO_VR_TN_ANL,
      Entitlements.LIO_DZ_SN_ANL,
      Entitlements.LIO_VR_SN_ANL
    ];

    const investmentCorporatesPermissions = [
      Entitlements.LIO_FK_DZ_TA_ANL,
      Entitlements.LIO_FK_VR_TA_ANL,
      Entitlements.LIO_FK_DZ_TE_ANL,
      Entitlements.LIO_FK_VR_TE_ANL,
      Entitlements.LIO_FK_DZ_ON_ANL,
      Entitlements.LIO_FK_VR_ON_ANL,
      Entitlements.LIO_FK_DZ_TN_ANL,
      Entitlements.LIO_FK_VR_TN_ANL,
      Entitlements.LIO_FK_DZ_SN_ANL,
      Entitlements.LIO_FK_VR_SN_ANL
    ];

    const investment100Permissions = [
      Entitlements.LIO_DZ_TE_ANL,
      Entitlements.LIO_VR_TE_ANL,
      Entitlements.LIO_DZ_ON_ANL,
      Entitlements.LIO_VR_ON_ANL,
      Entitlements.LIO_DZ_TN_ANL,
      Entitlements.LIO_VR_TN_ANL,
      Entitlements.LIO_DZ_SN_ANL,
      Entitlements.LIO_VR_SN_ANL
    ];

    const raisingPermissions = [
      Entitlements.LIO_DZ_TA_AUF,
      Entitlements.LIO_VR_TA_AUF,
      Entitlements.LIO_DZ_TE_AUF,
      Entitlements.LIO_VR_TE_AUF,
      Entitlements.LIO_DZ_ON_AUF,
      Entitlements.LIO_VR_ON_AUF,
      Entitlements.LIO_DZ_TN_AUF,
      Entitlements.LIO_VR_TN_AUF,
      Entitlements.LIO_DZ_SN_AUF,
      Entitlements.LIO_VR_SN_AUF
    ];

    const refiPermissions = [Entitlements.LIO_DZ_REFI, Entitlements.LIO_VR_REFI];

    const cancellationPermissions = [Entitlements.LIO_DEAL_STORNO];

    const cancellationCorporatesPermissions = [Entitlements.LIO_FK_DEAL_STORNO];

    const businessStatusPermissions = [
      Entitlements.LIO_VR_REFI,
      Entitlements.LIO_DEAL_ADMIN,
      Entitlements.LIO_VR_TA_AUF,
      Entitlements.LIO_VR_TE_AUF,
      Entitlements.LIO_DZ_TA_AUF,
      Entitlements.LIO_DZ_TE_AUF,
      Entitlements.LIO_DZ_ON_AUF,
      Entitlements.LIO_DZ_TN_AUF,
      Entitlements.LIO_DZ_SN_AUF,
      Entitlements.LIO_VR_TA_ANL,
      Entitlements.LIO_VR_TE_ANL,
      Entitlements.LIO_DZ_TA_ANL,
      Entitlements.LIO_DZ_TE_ANL,
      Entitlements.LIO_DZ_ON_ANL,
      Entitlements.LIO_DZ_TN_ANL,
      Entitlements.LIO_DZ_SN_ANL,
      Entitlements.LIO_REQ_REPORT_VRB
    ];

    const businessStatusCorporatesPermissions = [
      Entitlements.LIO_FK_VR_TA_ANL,
      Entitlements.LIO_FK_VR_TE_ANL,
      Entitlements.LIO_FK_DZ_TA_ANL,
      Entitlements.LIO_FK_DZ_TE_ANL,
      Entitlements.LIO_FK_DZ_ON_ANL,
      Entitlements.LIO_FK_DZ_TN_ANL,
      Entitlements.LIO_FK_DZ_SN_ANL,
      Entitlements.LIO_FK_REQ_REPORT_VRB
    ];

    const limitPermissions = [Entitlements.LIO_REQ_LIMIT];

    const evaluationPermissions = [Entitlements.LIO_REQ_REPORT_VRB, Entitlements.LIO_REQ_REPORT_DZ];

    const evaluationExtCorporatesPermissions = [Entitlements.LIO_FK_REQ_REPORT_VRB];
    const evaluationDzCorporatesPermissions = [Entitlements.LIO_FK_REQ_REPORT_DZ];

    const adminPermissions = [
      Entitlements.LIO_ADM_KD_KG,
      Entitlements.LIO_ADM_KD_KG_RO,
      Entitlements.LIO_ADM_FUS,
      Entitlements.LIO_SYS_TRADE_TIME,
      Entitlements.LIO_SYS_TRADE_STOP,
      Entitlements.LIO_ADM_BETRAG,
      Entitlements.LIO_DEAL_ADMIN,
      Entitlements.LIO_ADM_TRIGGER,
      Entitlements.LIO_ADM_ZINSG,
      Entitlements.LIO_ADM_MDS,
      Entitlements.LIO_ADM_MDS_ABOS,
      Entitlements.LIO_ADM_MDS_VERBINDUNG,
      Entitlements.LIO_ADM_KURSLISTEN,
      Entitlements.LIO_ADM_EMAIL,
      Entitlements.LIO_ADM_FEIERTAGE,
      Entitlements.LIO_ADM_CMS,
      Entitlements.LIO_ADM_MARGIN
    ];

    const adminCorporatesPermissions = [
      Entitlements.LIO_FK_ADM_KD_KG,
      Entitlements.LIO_FK_ADM_ZINSG,
      Entitlements.LIO_FK_ADM_FEIERTAGE,
      Entitlements.LIO_FK_ADM_CMS,
      Entitlements.LIO_FK_ADM_MARGIN,
      Entitlements.LIO_FK_DZ_LIMIT_ADM,
      Entitlements.LIO_FK_DZ_LIMIT_RO
    ];

    const marginPermissions = [Entitlements.LIO_MARGEN, Entitlements.LIO_MARGEN_RO];
    const marginCorporatesPermissions = [Entitlements.LIO_FK_MARGEN, Entitlements.LIO_FK_MARGEN_RO];

    const maturityPermissions = [Entitlements.LIO_LAUFZEITEN, Entitlements.LIO_LAUFZEITEN_RO];
    const maturityCorporatesPermissions = [Entitlements.LIO_FK_LAUFZEITEN, Entitlements.LIO_FK_LAUFZEITEN_RO];

    const logPermission = [Entitlements.LIO_DEAL_ADMIN, Entitlements.LIO_FK_DEAL_ADMIN];

    return {
      isMatchedEntitlement(...entitlements: Entitlements[]) {
        const userEntitlements = state.entitlements();
        return userEntitlements.length > 0 && entitlements.some((e) => userEntitlements.includes(e));
      },
      _loadEntitlements() {
        state._entitlementsService.entitlementsGet(state._authService.roles, state._trackingIdService.generateTrackingId()).subscribe({
          next: (resp) => {
            state._simpleMessageService.clearInlineMessage(state._MESSAGE_KEY);
            patchState(state, { entitlements: resp.entitlements });
            this._loadPermissions();
          },
          error: (err) => {
            console.log(`Fehler beim Laden der Entitlements: ${err.message}`);
            state._simpleMessageService.sendInlineErrorMessage(
              state._MESSAGE_KEY,
              'Ihre Berechtigungen konnten nicht geladen werden. Bitte wenden Sie sich an den LiOn-Administrator.'
            );
          }
        });
      },
      _loadPermissions() {
        console.log('Setting permissions ...');

        patchState(state, {
          isDzSysAdmin: state._authService.hasRoles(Roles.LIO_DZ_SYSADMIN)
        });
        patchState(state, {
          hasPermitAll: state._authService.hasRoles(Roles.PERMIT_ALL)
        });
        patchState(state, {
          isFkDzTrader: state._authService.hasRoles(Roles.LIO_FK_DZ_TRADER)
        });
        patchState(state, {
          isFkTrader: state._authService.hasRoles(Roles.LICO_TRADER)
        });
        patchState(state, {
          isDzTrader: state._authService.hasRoles(Roles.LIO_DZ_TRADER_1, Roles.LIO_DZ_TRADER_2)
        });

        state._konfigurationService.konfigurationGet('LIO_REFI_FUER_VR_ANZEIGEN').subscribe((response) => {
          const val = response.konfiguration?.value?.toLowerCase();
          patchState(state, {
            isRefiVisibleForVr: val === '1' || val === 'true'
          });
        });

        state._konfigurationService.konfigurationGet('LIO_EVALUATION_FUER_FK_ANZEIGEN').subscribe((response) => {
          const val = response.konfiguration?.value?.toLowerCase();
          patchState(state, {
            isEvaluationVisibleForFk: val === '1' || val === 'true'
          });
        });

        patchState(state, {
          hasInvestmentPermission: this.isMatchedEntitlement(...investmentPermissions),
          hasCorporatesInvestmentPermission: this.isMatchedEntitlement(...investmentCorporatesPermissions),
          hasInvestment100Permission: this.isMatchedEntitlement(...investment100Permissions),
          hasRaisingPermission: this.isMatchedEntitlement(...raisingPermissions),
          hasRefiPermission: this.isMatchedEntitlement(...refiPermissions),
          hasCancellationPermission: this.isMatchedEntitlement(...cancellationPermissions),
          hasCorporatesCancellationPermission: this.isMatchedEntitlement(...cancellationCorporatesPermissions),
          hasBusinessStatusPermission: this.isMatchedEntitlement(...businessStatusPermissions),
          hasCorporatesBusinessStatusPermission: this.isMatchedEntitlement(...businessStatusCorporatesPermissions),
          hasLimitPermission: this.isMatchedEntitlement(...limitPermissions),
          hasEvaluationPermission: this.isMatchedEntitlement(...evaluationPermissions),
          hasCorporatesExtEvaluationPermission: this.isMatchedEntitlement(...evaluationExtCorporatesPermissions),
          hasCorporatesDzEvaluationPermission: this.isMatchedEntitlement(...evaluationDzCorporatesPermissions),
          hasAdminPermission: this.isMatchedEntitlement(...adminPermissions),
          hasCorporatesAdminPermission: this.isMatchedEntitlement(...adminCorporatesPermissions),
          hasMarginPermission: this.isMatchedEntitlement(...marginPermissions),
          hasCorporatesMarginPermission: this.isMatchedEntitlement(...marginCorporatesPermissions),
          hasMaturityPermission: this.isMatchedEntitlement(...maturityPermissions),
          hasCorporatesMaturityPermission: this.isMatchedEntitlement(...maturityCorporatesPermissions),
          hasTaskLogPermission: this.isMatchedEntitlement(...logPermission)
        });

        patchState(state, {
          name: state._authService.name,
          username: state._authService.username,
          userMainGpNumber: state._authService.zgpnrs[0],
          userGruppe: state._authService.gruppe
        });

        patchState(state, { loaded: true });
      }
    };
  }),

  withHooks({
    onInit(state) {
      const authService = inject(AuthService);
      effect(() => {
        const authenticated = authService.authenticated();
        untracked(() => {
          if (!authenticated) {
            return;
          }

          if (authenticated && Object.keys(authService.roles).length > 0) {
            console.log('Loading entitlements ...');
            patchState(state, { roles: authService.roles });
            state._loadEntitlements();
          }
        });
      });
    }
  })
);
